import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    useCanUseSmsHandoff,
    useIsInAppBrowser,
    useUnsafeEffect,
} from "@wags/tanstack-start/react";
import type { JsonValue } from "@wags/utils/json.ts";
import { usePostHog } from "posthog-js/react";
import { useRef, useState, useSyncExternalStore } from "react";
import {
    CONTACT_FUNNEL_EVENTS,
    type ContactFunnelProperties,
    captureContactFunnelEvent,
} from "../contact-funnel";
import { captureReachOutAction } from "./analytics";
import {
    contactSessionKey,
    contactVerificationQuery,
} from "./contact-session-client";
import { attemptEvidence, newContactAttemptId } from "./handoff-evidence";
import { createHandoffObserver } from "./handoff-observer";
import type IHandoffRuntime from "./handoff-runtime.interface";
import {
    captureMissingSession,
    capturingContactSessionQuery,
    sendToMessages,
} from "./messages-handoff";

function browserHandoffRuntime(): IHandoffRuntime {
    return {
        now: () => performance.now(),
        visibility: () => document.visibilityState,
        onPagehide: (listener) => {
            window.addEventListener("pagehide", listener);
            return () => window.removeEventListener("pagehide", listener);
        },
        onVisibilityChange: (listener) => {
            document.addEventListener("visibilitychange", listener);
            return () =>
                document.removeEventListener("visibilitychange", listener);
        },
        after: (delayMs, listener) => {
            const timer = window.setTimeout(listener, delayMs);
            return () => window.clearTimeout(timer);
        },
    };
}

const RESUME_KEY = "wags-contact-one-tap-resume";

function readResumeChallenge(
    listingId: string,
    authUserId: string,
): string | null {
    try {
        const saved = JSON.parse(
            window.sessionStorage.getItem(RESUME_KEY) ?? "null",
        );
        return saved?.listingId === listingId &&
            saved?.authUserId === authUserId &&
            typeof saved.challengeId === "string"
            ? saved.challengeId
            : null;
    } catch {
        return null;
    }
}

function writeResume(
    value: {
        challengeId: string;
        listingId: string;
        authUserId: string;
    } | null,
) {
    try {
        if (value)
            window.sessionStorage.setItem(RESUME_KEY, JSON.stringify(value));
        else window.sessionStorage.removeItem(RESUME_KEY);
    } catch {}
}

const subscribeNever = () => () => {};

export function useOneTapContact({
    listingId,
    authUserId,
    shared,
    onVerified,
}: {
    listingId: string;
    authUserId: string;
    shared: ContactFunnelProperties;
    onVerified: () => Promise<void>;
}) {
    const posthog = usePostHog();
    const queryClient = useQueryClient();
    const canUseSmsHandoff = useCanUseSmsHandoff();
    const inAppBrowser = useIsInAppBrowser();
    const [scope, setScope] = useState({ listingId, authUserId });
    if (scope.listingId !== listingId || scope.authUserId !== authUserId)
        setScope({ listingId, authUserId });
    const [pendingFor, setPendingFor] = useState<{
        scope: typeof scope;
        attemptId: string;
    } | null>(null);
    const [challenge, setChallenge] = useState<{
        scope: typeof scope;
        id: string;
    } | null>(null);
    const active = useRef<{
        pending: boolean;
        attemptId: string;
        challengeId?: string;
        wroteResume: boolean;
        observer: ReturnType<typeof createHandoffObserver>;
    } | null>(null);
    const mounted = useRef(false);
    const currentScope = useRef<typeof scope | null>(null);
    const cancelActive = (reason: "verified" | "replaced" | "unmounted") => {
        const cancelled = active.current;
        active.current = null;
        cancelled?.observer.cancel(reason);
        if (reason !== "unmounted" && cancelled?.pending)
            setPendingFor((current) =>
                current?.attemptId === cancelled.attemptId ? null : current,
            );
    };

    useUnsafeEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            cancelActive("unmounted");
        };
    }, []);
    useUnsafeEffect(() => {
        currentScope.current = scope;
        return () => {
            if (currentScope.current === scope) currentScope.current = null;
            if (mounted.current) cancelActive("replaced");
        };
    }, [scope]);

    const resumeChallenge = useSyncExternalStore(
        subscribeNever,
        () => readResumeChallenge(listingId, authUserId),
        () => null,
    );
    const challengeId =
        challenge?.scope === scope ? challenge.id : resumeChallenge;
    async function handleVerified() {
        if (currentScope.current !== scope) return;
        const ownedChallenge = active.current
            ? active.current.challengeId
            : readResumeChallenge(listingId, authUserId);
        if (!challengeId || ownedChallenge !== challengeId) return;
        if (active.current) cancelActive("verified");
        setChallenge(null);
        writeResume(null);
        await onVerified();
    }
    const verificationOptions = contactVerificationQuery(
        challengeId ?? undefined,
        true,
    );
    useQuery({
        ...verificationOptions,
        queryFn: async () => {
            const status = await verificationOptions.queryFn();
            if (status.status === "verified") await handleVerified();
            return status;
        },
    });

    async function handOff(
        fallback: (handedOff: boolean) => void,
        tapShared: ContactFunnelProperties,
        operation: NonNullable<typeof active.current>,
    ) {
        const { attemptId } = operation;
        const capture = (
            event: string,
            properties: Record<string, JsonValue>,
        ) => captureContactFunnelEvent(posthog, event, tapShared, properties);

        async function requestSession() {
            try {
                return await queryClient.fetchQuery(
                    capturingContactSessionQuery({
                        authUserId,
                        listingId,
                        trigger: "open_messages",
                        capture,
                        fetchImpl: fetch,
                    }),
                );
            } catch {
                return null;
            }
        }

        captureReachOutAction(posthog, {
            listingId,
            requirementKinds: [],
            ...tapShared,
            action: "open_messages",
            ...attemptEvidence(attemptId, "one_tap_v2"),
            session_state: "unresolved",
        });
        const sessionPrefetched =
            queryClient.getQueryData(
                contactSessionKey(authUserId, listingId),
            ) !== undefined;
        const session = await requestSession();
        if (active.current !== operation) return;
        setPendingFor(null);
        operation.pending = false;
        if (!session) {
            active.current = null;
            captureMissingSession(
                capture,
                sessionPrefetched,
                attemptId,
                "one_tap_v2",
            );
            fallback(false);
            return;
        }
        operation.challengeId = session.verification.id;
        operation.observer.run(session.session.id, (navigationRequested) =>
            sendToMessages({
                session,
                verificationStatus: undefined,
                sessionPrefetched,
                canUseSmsHandoff,
                capture,
                fetchImpl: fetch,
                attemptId,
                observationMode: "one_tap_v2",
                onNavigationRequested: () => {
                    operation.wroteResume = true;
                    setChallenge({ scope, id: session.verification.id });
                    writeResume({
                        challengeId: session.verification.id,
                        listingId,
                        authUserId,
                    });
                    navigationRequested();
                },
            }),
        );
    }

    function open(
        fallback: (handedOff: boolean) => void,
        source = shared.source,
    ) {
        if (currentScope.current !== scope) return false;
        if (active.current?.pending) return false;
        cancelActive("replaced");
        const tapShared = { ...shared, source };
        const attemptId = newContactAttemptId();
        const operation: NonNullable<typeof active.current> = {
            pending: true,
            attemptId,
            challengeId: challengeId ?? undefined,
            wroteResume: false,
            observer: createHandoffObserver({
                runtime: browserHandoffRuntime(),
                attemptId,
                capture: (observation) => {
                    captureContactFunnelEvent(
                        posthog,
                        CONTACT_FUNNEL_EVENTS.handoffObservation,
                        tapShared,
                        observation,
                    );
                    if (observation.evidence === "visible_at_deadline")
                        captureContactFunnelEvent(
                            posthog,
                            CONTACT_FUNNEL_EVENTS.handoffStuck,
                            tapShared,
                            observation,
                        );
                },
                fallback: (handedOff) => {
                    if (active.current !== operation) return;
                    if (!handedOff && operation.wroteResume) {
                        setChallenge(null);
                        writeResume(null);
                    }
                    fallback(handedOff);
                },
            }),
        };
        active.current = operation;
        setPendingFor({ scope, attemptId });
        void handOff(fallback, tapShared, operation);
        return true;
    }

    return {
        available: canUseSmsHandoff && !inAppBrowser,
        pending: pendingFor?.scope === scope,
        open,
    };
}
